import { Button } from '@heroui/react';
import React, { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContentLoadError, PageWrapper, Spinner } from '../../../components';
import { useHeroToast } from '../../../hooks';
import { useGetUser, useQueryHelper, useUpdateUser } from '../../../queries';
import { UserDataFirebasePatchRequest } from '../../../types';
import { CreateUserForm } from '../components';

export const EditUser: FC = () => {
  const { id } = useParams();
  const { showPromise, showError } = useHeroToast();
  const { mutate, isPending } = useUpdateUser();
  const navigate = useNavigate();

  const userDocument = useGetUser(id);
  const { data: user, refetch } = userDocument;
  const { isLoading, isError, errorMessage } = useQueryHelper({
    queries: [userDocument],
  });

  const handleSubmit = (data : UserDataFirebasePatchRequest) : void => {
    showPromise(
      () => new Promise(
        (resolve, reject) => {
          mutate(
            { data },
            {
              onSuccess: resolve,
              onError: reject,
            },
          );
        },
      ),
      <Spinner />,
    ).then(() => {
      navigate('/users');
    }).catch((err) => {
      showError(err.message || 'Request Error');
    });
  };

  if (!id) {
    return (
      <PageWrapper>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} message="Error url param" />
      </PageWrapper>
    );
  }

  if (isError) {
    return (
      <PageWrapper>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} message={errorMessage} />
      </PageWrapper>
    );
  }

  if (isLoading) {
    return <Spinner global />;
  }

  return (
    <PageWrapper>
      <CreateUserForm
        onSubmit={(userDataRequest) => handleSubmit({
          uid: id,
          data: userDataRequest,
        })}
        isEdit
        initialValue={user}
      />
      <div className="flex justify-end gap-3 mt-6 pr-6 pb-6">
        <Button
          color="default"
          variant="light"
          onPress={() => navigate('/users')}
          isDisabled={isPending}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          type="submit"
          form="form-create-user"
          isLoading={isPending}
          isDisabled={isPending}
        >
          {isPending ? 'Updating...' : 'Update User'}
        </Button>
      </div>
    </PageWrapper>
  );
};
